import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { OpenInPostHogButton } from '@/components/OpenInPostHogButton';
import { posthogTrendUrl } from '@/lib/posthogLinks';
import { trackEvents } from '@/lib/events';

/** GA4 UI — sem id numérico de property/account em mãos, abre a home do GA4
 *  (o operador escolhe a property do admin, stream G-Q0ZMBQ7Q4Y). */
const GA4_HOME_URL = 'https://analytics.google.com/analytics/web/';

/**
 * "Uso do admin" — o terceiro plano de analytics: a telemetria do PRÓPRIO
 * admin (navegação e ações do super-admin: `bi_dashboard_viewed`, `login`,
 * `bi_csv_exported`, deep-links…). Diferente do BI de negócio e do BI de
 * produto, este uso NÃO é pré-agregado no Firestore — vive cru no PostHog e
 * no GA4. Esta tela é o ponto de entrada para esses dois, não um dashboard
 * agregado.
 *
 * Origem: #68 — 2026-06-20 13:13 +0200 (navegação em 2 grupos de BI).
 *
 * Contrato: só links (PostHog via `posthogTrendUrl`, home do GA4); não lê nem
 * grava nada no Firestore.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/admin-usage`).
 */
export function AdminUsageDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('adminUsage.subtitle')}>
          {t('adminUsage.title')}
        </Header>
      }
    >
      <Container header={<Header variant="h2">{t('adminUsage.sourcesTitle')}</Header>}>
        <SpaceBetween size="l">
          <Box variant="p" color="text-body-secondary">
            {t('adminUsage.explanation')}
          </Box>

          <Box>
            <SpaceBetween size="xs">
              <Box variant="h3">{t('adminUsage.posthogTitle')}</Box>
              <Box variant="p" color="text-body-secondary">
                {t('adminUsage.posthogHint')}
              </Box>
              <OpenInPostHogButton
                href={posthogTrendUrl([
                  { id: 'bi_dashboard_viewed' },
                  { id: 'login' },
                ])}
                surface="admin_usage"
              />
            </SpaceBetween>
          </Box>

          <Box>
            <SpaceBetween size="xs">
              <Box variant="h3">{t('adminUsage.ga4Title')}</Box>
              <Box variant="p" color="text-body-secondary">
                {t('adminUsage.ga4Hint')}
              </Box>
              <Button
                href={GA4_HOME_URL}
                target="_blank"
                iconName="external"
                iconAlign="right"
                onClick={(): void => {
                  trackEvents.biPostHogDeeplinkOpened('admin_usage_ga4');
                }}
              >
                {t('adminUsage.openGa4')}
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
}
